import { TreeNode } from "@/types/tree";
import { getKeywordsForImages } from "./keywords";
import {
  getImageControlsAndInfoGroupedByNodeId,
  setImageControlsAndInfoOnNodes,
  getFutureLayerImageMetaFromNode,
  updateValueOnNode
} from "./meta";
import { callImageService } from "../api/image-bridge";
import { treeNodeReduce } from "@/lib/tree/tree";
import { ImageControlAndInfo, UpdateMode } from "./types";
import { getLikelyWidthForImage, getOptimalImageUrl } from "./dimensions";
import { UnsplashImage } from "../api/unsplash";
import {
  shouldDownloadImageToMediaLibrary,
  downloadImageToMediaLibrary,
  getMediaInputValueFromMediaUploadResponse
} from "./media-library";
import { selectUnusedImage } from "./util";
import { ELEMENTS_TO_IGNORE } from "@/futurelayer/ai";
import { isFutureLayerMode } from "@/futurelayer/ui/ui";
import { configStore } from "@/store/store";

const FALLBACK_BLACK_SQUARE_IMAGE_URL = "https://app.futurelayer.ai/app/black-square-20x20.png";


export async function setStockPhotoSearchKeywordsForImageControlsInASection(
  section: TreeNode,
  mode: UpdateMode,
  abortController: AbortController
) {
  const keywordsForImages = (
    await getKeywordsForImages(section, abortController)
  ).response;
  const groupedData = getImageControlsAndInfoGroupedByNodeId(keywordsForImages);
  setImageControlsAndInfoOnNodes(groupedData, mode, section);
}

export async function setStockPhotoSearchResultsForImageControlsInASection(
  section: TreeNode,
  mode: UpdateMode,
  abortController: AbortController
) {
  const allImageControls = treeNodeReduce(
    (node, acc) => {
      const element = configStore.element(node.data.type);
      if (element.settings && element.settings.disableAI) return acc;
      if (ELEMENTS_TO_IGNORE.includes(node.data.type) && isFutureLayerMode()) return acc;

      const nodeData = getFutureLayerImageMetaFromNode(node);
      if (nodeData.length > 0) {
        acc.push({
          nodeId: node.id,
          imageControls: nodeData
        });
      }
      return acc;
    },
    section,
    [] as { nodeId: TreeNode["id"]; imageControls: ImageControlAndInfo[] }[]
  );

  for (const { nodeId, imageControls } of allImageControls) {
    const updatedImageControls = await Promise.all(
      imageControls.map(async control => {
        let searchResults: UnsplashImage[] = [];

        try {
          searchResults = await callImageService(
            {
              query: control.keywords[0],
              page: 1,
              per_page: 25
            },
            abortController
          );
        } catch (error) {
          console.error("Image service failed for query:", control.keywords[0]);
          console.error("Error details:", error);

          // Return a fallback black square image
          searchResults = [{
            id: "fallback-black-square",
            description: "Fallback image due to service error",
            alt_description: "Black square placeholder",
            urls: {
              raw: FALLBACK_BLACK_SQUARE_IMAGE_URL,
              full: FALLBACK_BLACK_SQUARE_IMAGE_URL,
              regular: FALLBACK_BLACK_SQUARE_IMAGE_URL,
              small: FALLBACK_BLACK_SQUARE_IMAGE_URL,
              thumb: FALLBACK_BLACK_SQUARE_IMAGE_URL
            },
            user: {
              name: "Fallback",
              username: "fallback"
            },
            width: 1000,
            height: 1000
          }];
        }

        return {
          ...control,
          searchResults
        };
      })
    );

    updateValueOnNode(
      nodeId,
      "meta.futureLayer.images",
      updatedImageControls,
      `Updated search results for node "${nodeId}"`,
      mode,
      section
    );
  }
}

export async function setImageControlsInSectionToStockPhotoSearchResults(
  section: TreeNode,
  mode: UpdateMode,
  abortController: AbortController
) {
  const allImageControls = treeNodeReduce(
    (node, acc) => {
      const nodeData = getFutureLayerImageMetaFromNode(node);
      if (nodeData.length > 0) {
        acc.push({
          node: node,
          imageControls: nodeData
        });
      }
      return acc;
    },
    section,
    [] as { node: TreeNode; imageControls: ImageControlAndInfo[] }[]
  );

  const imageUpdatePromises = [];

  for (const { node, imageControls } of allImageControls) {
    for (const control of imageControls) {
      if (control.searchResults.length > 0) {
        const img = await selectUnusedImage(control.searchResults, control.controlPath, abortController);

        console.log("setImageInSectionToSearchResult", node.id, control, img);

        imageUpdatePromises.push(() => {
          return setImageInSectionToSearchResult(
            node,
            control,
            img,
            mode,
            section,
            abortController
          );
        });
      }
    }
  }

  await Promise.all(imageUpdatePromises.map(fn => fn()));
}

export async function setImageInSectionToSearchResult(
  node: TreeNode,
  imageControl: ImageControlAndInfo,
  img: UnsplashImage,
  mode: UpdateMode,
  section: TreeNode,
  abortController: AbortController
) {

  const width = getLikelyWidthForImage(node);
  const optimalUrl = getOptimalImageUrl(img, width);

  if (shouldDownloadImageToMediaLibrary(img)) {
    // make it load in the UI instantly by hotlinking first
    updateValueOnNode(
      node.id,
      imageControl.controlPath,
      {
        id: -1,
        type: "external_image",
        url: optimalUrl,
        alt: img.alt_description || "",
        caption: img.description || ""
      },
      `Update image at ${imageControl.controlPath}`,
      mode,
      section
    );

    const mediaLibraryResult = await downloadImageToMediaLibrary(
      optimalUrl,
      img,
      abortController
    );
    if (mediaLibraryResult) {
      updateValueOnNode(
        node.id,
        imageControl.controlPath,
        getMediaInputValueFromMediaUploadResponse(mediaLibraryResult),
        `Update image at ${imageControl.controlPath} with downloaded image`,
        mode,
        section
      );
      return;
    } else {
      console.warn(
        `Failed to download image for ${imageControl.controlPath}, falling back to external image`
      );
    }
  }

  updateValueOnNode(
    node.id,
    imageControl.controlPath,
    {
      id: -1,
      type: "external_image",
      url: optimalUrl,
      alt: img.alt_description || "",
      caption: img.description || ""
    },
    `Update image at ${imageControl.controlPath}`,
    mode,
    section
  );
}
